/**
 * `ConsentGate` — GDPR consent gate shown BEFORE the pre-chat form when
 * `WidgetConfig.gdpr.enabled` (ARCHITECTURE §7). Renders the org's consent copy,
 * an optional privacy-policy link, and an "I agree" button. On agree it records
 * the acceptance anonymously (pre-session) via `api.widget.submitConsent(cid)` and
 * then calls `onAgree()` so `PreChatForm` swaps to the normal form.
 *
 * The `cid` is resolved from the embed itself (`parseEmbed`), mirroring how
 * {@link EndedScreen}/{@link CsatRating} derive theirs. A submit failure surfaces
 * inline and leaves the gate in place for a retry (same idiom as `CsatRating`) —
 * the consent record is the whole point of the gate, so we don't skip past a
 * failed write.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { GdprConfig } from '@flozic/shared/types';
import { parseEmbed } from '../config/parseEmbed';
import { getWidgetApi } from '../config/loadWidgetConfig';

export interface ConsentGateProps {
  /** The org's GDPR consent-gate config (already known to be `enabled`). */
  gdpr: GdprConfig;
  /** Called once consent has been recorded, to advance to the pre-chat form. */
  onAgree: () => void;
}

type Status = 'idle' | 'submitting';

export function ConsentGate({ gdpr, onAgree }: ConsentGateProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  // Guard against setState after unmount (panel closed mid-submit). Re-arm on every
  // (re)mount so React 18 StrictMode's dev double-mount doesn't wedge it at false.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Resolve the embed id once; if it can't be parsed we still let the visitor proceed
  // (the widget wouldn't have mounted without a valid embed, so this is just defensive).
  const cid = useMemo<string | null>(() => {
    try {
      return parseEmbed().cid;
    } catch {
      return null;
    }
  }, []);

  const buttonLabel = gdpr.buttonText?.trim() || 'I agree';
  const privacyLabel = gdpr.privacyText?.trim() || 'Privacy policy';

  const agree = async (): Promise<void> => {
    if (status === 'submitting') return;
    setStatus('submitting');
    setError(null);
    try {
      if (cid) await getWidgetApi().widget.submitConsent(cid);
      onAgree();
    } catch {
      if (mounted.current) {
        setStatus('idle');
        setError('Could not record your consent. Please try again.');
      }
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      <p className="whitespace-pre-wrap text-sm text-lc-text/80">{gdpr.content}</p>

      {gdpr.privacyUrl ? (
        <a
          href={gdpr.privacyUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="text-sm font-medium text-lc-primary underline"
        >
          {privacyLabel}
        </a>
      ) : null}

      {error ? (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        onClick={agree}
        disabled={status === 'submitting'}
        className="mt-1 min-h-[2.5rem] rounded-lc bg-lc-primary px-4 py-2 text-sm font-medium text-lc-on-primary disabled:opacity-50"
      >
        {status === 'submitting' ? 'One moment…' : buttonLabel}
      </button>
    </div>
  );
}
